import { useRef, useState } from 'react';
import { navigateTo } from '@/core/navigation';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { phase1Service } from '@/core/services/phase1';
import { phase2Service } from '@/core/services/phase2';
import { trialBalanceService } from '@/core/services/trialBalance';
import { formatNatural } from '@/core/format/naturalBalance';
import type { ChartAccountRow } from '@/core/models/trial-balance.models';
import type { JournalNominalOption, JournalTypeOption } from '@/core/models/phase1.models';
import {
  EVIDENCE_FILE_ACCEPT,
  EVIDENCE_FILE_MAX_BYTES,
  type WorkingPaperEvidenceRow,
} from '@/core/models/phase2.models';
import { Button } from '@/kit/Button';
import { DateField } from '@/kit/DateField';
import { InputField, TextareaField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select } from '@/kit/Select';
import { validateForm } from '@/features/manage/formRules';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { CancelButton, fmt2, numberFromInput, numberToInput, useAlive, useLive, useOnInit } from './common';
import './JournalDialog.scss';

export interface JournalDialogData {
  periodId: string;
  clientId: string;
  journalId: string | null;
  readOnly: boolean;
  types: JournalTypeOption[];
  defaultDate: string | null;
  /** APT338: a journal started from a working paper opens with these values (a new journal only); the user can change all of them. */
  prefill?: JournalPrefill | null;
  /**
   * APT338 follow-up 2 (the client, 28 Oct 2026 21:35: "for that journal they can add evidences & explanations"): set when a NEW journal is started from a working
   * paper. The journal then needs an explanation and can carry evidence files; they are attached to the journal (and kept on the schedule) once it is saved.
   * `ensureSchedule` saves the lead schedule first when it has no row yet, because evidence hangs off the schedule.
   */
  evidence?: JournalEvidenceTarget | null;
}

export interface JournalEvidenceTarget {
  scheduleType: number;
  ensureSchedule: () => Promise<void>;
}

/** The longest explanation the evidence table holds (ap.WorkingPaperEvidence.Detail is NVARCHAR(4000)). */
export const JOURNAL_EXPLANATION_MAX = 4000;

/**
 * APT338 (the client: "provide an option to create journals as per the working papers"): the values a new journal opens with when it is started from a
 * working paper - its type, date, narrative and lines. A line's nominal is null when the user is to choose it.
 */
export interface JournalPrefill {
  typeCode: string;
  journalDate: string;
  narrative: string;
  lines: Array<{ nominalAccountId: string | null; debit: number; credit: number; narrative: string | null }>;
}

interface EditorLine {
  /** React key only (Angular tracked the objects themselves). */
  uid: number;
  nominalAccountId: string | null;
  debit: number | null;
  credit: number | null;
  narrative: string | null;
  /** ap5-a6: key into {@link NominalPick}; a line may point at a chart account with no nominal yet. */
  pickKey: string | null;
  /** The amount boxes keep what was typed ("12." must stay "12."); the numbers above are what is saved. */
  debitText?: string;
  creditText?: string;
}

/** The longest narrative the journal and its lines hold (NVARCHAR(500)). */
export const JOURNAL_NARRATIVE_MAX = 500;

/** Amount columns are DECIMAL(19,4): 15 whole digits and 4 decimals. */
const AMOUNT_BOX = { charMode: 'number', allowDecimal: true, maxIntDigits: 15, maxDecimals: 4 } as const;

/** The number behind amount text; '' and a lone '.' are no amount. */
const amountFromText = (text: string): number | null => {
  const value = numberFromInput(text);
  return value === null || Number.isNaN(value) ? null : value;
};

/**
 * ap5-a6: one row of the nominal picker. `nominalAccountId` is set for nominals the period already holds; chart-only
 * rows carry `chartAccountId` instead and are turned into period nominals on save.
 */
export interface NominalPick {
  key: string;
  nominalAccountId: string | null;
  chartAccountId: string | null;
  code: string;
  description: string;
  adjustedAmount: number | null;
  priorYearAmount: number | null;
  /** APT349: the nominal's natural side from its category (1 debit, -1 credit), whether or not it has a reporting code; null only when it has no nature. */
  normalSign: number | null;
  inPeriod: boolean;
  /** Pre-built so the picker can search code and name with one `searchable` field. */
  searchText: string;
}

interface Model {
  typeCode: string;
  journalDate: string;
  narrative: string;
  lines: EditorLine[];
  picks: NominalPick[];
  isLoadingPicks: boolean;
  /** Status of the journal loaded for editing; anything but Draft (0) is shown read-only. */
  loadedStatus: number | null;
  /** APT338 follow-up 2: the explanation and the evidence files of a journal started from a working paper. */
  explanation: string;
  evidenceFiles: File[];
  /** APT338 follow-up 2: what an existing journal already carries (its explanation and evidence), read when it is opened. */
  journalEvidence: WorkingPaperEvidenceRow[];
  /** The date text the box could not read (a typo such as 31/02/2026); null when the date is fine. */
  dateInvalid: string | null;
  /** True after a save was refused: the mandatory fields that are still empty are outlined red. */
  submitted: boolean;
}

let nextUid = 1;
const newLine = (): EditorLine => ({
  uid: nextUid++,
  nominalAccountId: null,
  debit: 0,
  credit: 0,
  narrative: null,
  pickKey: null,
});

function buildPicks(nominals: JournalNominalOption[], chart: ChartAccountRow[]): NominalPick[] {
  const picks: NominalPick[] = nominals.map((n) => ({
    key: 'n:' + n.nominalAccountId,
    nominalAccountId: n.nominalAccountId,
    chartAccountId: null,
    code: n.code,
    description: n.displayDescription || n.description || '',
    adjustedAmount: n.adjustedAmount,
    priorYearAmount: n.priorYearAmount,
    normalSign: n.normalSign ?? null,
    inPeriod: true,
    searchText: `${n.code} ${n.displayDescription || n.description || ''}`,
  }));

  // Codes already in the period win; the chart only fills the gaps.
  const seen = new Set(picks.map((p) => p.code.trim().toLowerCase()));
  for (const account of chart) {
    const code = (account.code ?? '').trim();
    if (!code || seen.has(code.toLowerCase())) {
      continue;
    }
    seen.add(code.toLowerCase());
    picks.push({
      key: 'c:' + account.id,
      nominalAccountId: null,
      chartAccountId: account.id,
      code,
      description: account.accountName ?? '',
      adjustedAmount: null,
      priorYearAmount: null,
      normalSign: null,
      inPeriod: false,
      searchText: `${code} ${account.accountName ?? ''}`,
    });
  }

  picks.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  return picks;
}

/** Draft journal editor: balanced double entry, with comparative balances on the nominal picker. */
export function JournalDialog({ data, dialogRef }: DialogComponentProps<JournalDialogData, boolean>) {
  const alive = useAlive();
  // APT338: a journal started from a working paper opens filled in; an existing journal is loaded over it, as before.
  const prefill = data?.journalId ? null : (data?.prefill ?? null);
  const [m, set, ref] = useLive<Model>({
    typeCode: prefill?.typeCode ?? 'ADJ',
    journalDate: prefill?.journalDate ?? data?.defaultDate ?? '',
    narrative: prefill?.narrative ?? '',
    lines: prefill
      ? prefill.lines.map<EditorLine>((l) => ({
          uid: nextUid++,
          nominalAccountId: l.nominalAccountId,
          debit: l.debit,
          credit: l.credit,
          narrative: l.narrative,
          pickKey: l.nominalAccountId ? 'n:' + l.nominalAccountId : null,
        }))
      : [newLine(), newLine()],
    picks: [],
    isLoadingPicks: false,
    loadedStatus: null,
    explanation: '',
    evidenceFiles: [],
    journalEvidence: [],
    dateInvalid: null,
    submitted: false,
  });
  const isSaving = useRef(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const target = !data?.journalId ? (data?.evidence ?? null) : null;

  const pickFor = (line: EditorLine): NominalPick | null =>
    line.pickKey ? (ref.current.picks.find((p) => p.key === line.pickKey) ?? null) : null;

  /**
   * ap5-a6: the picker used to show only `PeriodJournals/getNominals`, which lists the nominals the committed trial
   * balance import created. Merge in the client's chart of accounts; anything not yet in the period is created on
   * demand when the journal is saved.
   */
  const loadPicks = async () => {
    set({ isLoadingPicks: true });
    try {
      // task-129: a failure here used to be swallowed, leaving an empty picker and no way to
      // tell a client with no chart from an request that did not come back. Keep the fallback
      // so one failing source still leaves the other usable, but say that it failed.
      let failed = false;
      const [nominals, chart] = await Promise.all([
        phase1Service.getJournalNominals(data!.periodId).catch(() => {
          failed = true;
          return [] as JournalNominalOption[];
        }),
        data!.clientId
          ? trialBalanceService
              .getAccounts(data!.clientId, { search: null, status: 1, accountTypeId: null })
              .catch(() => {
                failed = true;
                return [] as ChartAccountRow[];
              })
          : Promise.resolve([] as ChartAccountRow[]),
      ]);
      if (failed && alive.current) {
        notifyError('The nominal list could not be loaded in full. Close and reopen the journal to try again.');
      }
      if (!alive.current) return;
      set({ isLoadingPicks: false, picks: buildPicks(nominals ?? [], chart ?? []) });
    } catch (error) {
      if (!alive.current) return;
      set({ isLoadingPicks: false });
      notifyError(error);
    }
  };

  useOnInit(() => {
    if (data?.periodId) {
      void loadPicks();
    }

    if (data?.journalId) {
      // APT338 follow-up 2: the explanation and evidence the journal was made with. A failure here only leaves the list empty.
      void Promise.resolve(phase2Service.getJournalEvidence?.(data.periodId, data.journalId))
        .then((rows) => alive.current && set({ journalEvidence: rows ?? [] }))
        .catch(() => undefined);

      phase1Service
        .getJournal(data.periodId, data.journalId)
        .then((journal) => {
          if (!alive.current) return;
          const lines = (journal.lines ?? []).map<EditorLine>((l) => ({
            uid: nextUid++,
            nominalAccountId: l.nominalAccountId,
            debit: l.debit,
            credit: l.credit,
            narrative: l.narrative,
            pickKey: l.nominalAccountId ? 'n:' + l.nominalAccountId : null,
          }));
          set({
            typeCode: journal.typeCode,
            journalDate: journal.journalDate,
            narrative: journal.narrative ?? '',
            lines: lines.length === 0 ? [newLine(), newLine()] : lines,
            loadedStatus: journal.status ?? null,
          });
        })
        .catch((error) => {
          if (alive.current) notifyError(error);
        });
    }
  });

  const editLine = (uid: number, changes: Partial<EditorLine>) => {
    set({ lines: ref.current.lines.map((l) => (l.uid === uid ? { ...l, ...changes } : l)) });
  };

  const onPickChange = (line: EditorLine, pickKey: string | null) => {
    const pick = pickKey ? (ref.current.picks.find((p) => p.key === pickKey) ?? null) : null;
    // Chart-only picks have no nominal id until the journal is saved.
    editLine(line.uid, { pickKey, nominalAccountId: pick?.nominalAccountId ?? null });
  };

  /**
   * A posted journal is immutable on the server ('A posted journal is immutable. Post a reversal or a correcting
   * journal instead.'), so it opens read-only; only a Draft (status 0) can be edited.
   */
  const readOnly = Boolean(data?.readOnly) || (m.loadedStatus != null && m.loadedStatus !== 0);

  const totalDebit = m.lines.reduce((sum, l) => sum + Number(l.debit || 0), 0);
  const totalCredit = m.lines.reduce((sum, l) => sum + Number(l.credit || 0), 0);
  const difference = Number((totalDebit - totalCredit).toFixed(2));
  const isBalanced = Math.abs(difference) < 0.005;

  const addLine = () => set({ lines: [...ref.current.lines, newLine()] });

  /** task-129: a one-click way out of the empty-picker dead end, not just an explanation of it. */
  const openChartOfAccounts = () => {
    dialogRef.close();
    navigateTo('/home/taxreturns/settings-accoununts');
  };

  const removeLine = (index: number) => {
    const lines = ref.current.lines;
    if (lines.length > 2) {
      set({ lines: lines.filter((_, i) => i !== index) });
    }
  };

  const fail = (error: unknown) => {
    isSaving.current = false;
    if (alive.current) setBusy(false);
    notifyError(error);
  };

  const done = (message: string) => {
    notifySuccess(message);
    dialogRef.close(true);
  };

  /** APT338 follow-up 2: picks up the chosen files and checks the size of each before anything is sent. */
  const onEvidenceFilesChange = (input: HTMLInputElement) => {
    const chosen = Array.from(input.files ?? []);
    input.value = '';
    const tooBig = chosen.find((file) => file.size > EVIDENCE_FILE_MAX_BYTES);
    if (tooBig) {
      notifyError(`${tooBig.name} is larger than 10 MB. Each file must be 10 MB or smaller.`);
      return;
    }
    set({ evidenceFiles: [...ref.current.evidenceFiles, ...chosen] });
  };

  /**
   * APT338 follow-up 2: the explanation (a note) and the files are kept with the journal and on the working paper it was made from. The schedule is started first
   * when it has no row yet. The journal is already saved when this runs, so a failure is reported and the dialog still closes - never a second journal.
   */
  const attachEvidence = async (journalId: string): Promise<void> => {
    if (!target || !data) {
      return;
    }
    const s = ref.current;
    await target.ensureSchedule();
    await phase2Service.saveEvidence({
      accountingPeriodId: data.periodId,
      scheduleType: target.scheduleType,
      id: null,
      evidenceType: 'Note',
      title: 'Explanation of the adjustment journal',
      detail: s.explanation.trim(),
      storageRef: null,
      amount: null,
      journalId,
    });
    for (const file of s.evidenceFiles) {
      await phase2Service.uploadEvidenceFile(data.periodId, target.scheduleType, file, file.name, null, null, journalId);
    }
  };

  const finish = async (journalId: string, message: string) => {
    if (target) {
      try {
        await attachEvidence(journalId);
      } catch (error) {
        notifyError(error);
        notifyError('The journal was saved, but its explanation and evidence could not be attached. Add them to the working paper.');
      }
    }
    done(message);
  };

  /** Adds each chart-only account to the period, once, and points its lines at the new nominal. */
  const resolveChartPicks = async (lines: EditorLine[]): Promise<EditorLine[]> => {
    const pending = new Map<string, NominalPick>();
    for (const line of lines) {
      const pick = pickFor(line);
      if (pick && !pick.nominalAccountId && pick.chartAccountId) {
        pending.set(pick.key, pick);
      }
    }

    for (const pick of pending.values()) {
      const response = await phase1Service.ensureNominalFromChart(data!.periodId, {
        chartAccountId: pick.chartAccountId,
      });
      set({
        picks: ref.current.picks.map((p) =>
          p.key === pick.key ? { ...p, nominalAccountId: response.nominalAccountId, inPeriod: true } : p,
        ),
      });
    }

    const resolved = lines.map((line) => {
      const pick = pickFor(line);
      return pick?.nominalAccountId ? { ...line, nominalAccountId: pick.nominalAccountId } : line;
    });
    set({
      lines: ref.current.lines.map((l) => resolved.find((r) => r.uid === l.uid) ?? l),
    });
    return resolved;
  };

  const onSave = async (post: boolean) => {
    if (isSaving.current || !data?.periodId) {
      return;
    }
    const s = ref.current;

    set({ submitted: true });
    if (!s.journalDate || s.dateInvalid) {
      if (s.dateInvalid) {
        notifyError('Enter the journal date as dd/mm/yyyy, for example 31/03/2026.');
      } else {
        validateForm(false);
      }
      return;
    }

    // APT338 follow-up 2: a journal made from a working paper says why it is needed.
    if (target && !s.explanation.trim()) {
      validateForm(false);
      return;
    }
    if (target && s.explanation.trim().length > JOURNAL_EXPLANATION_MAX) {
      notifyError(`The explanation can be at most ${JOURNAL_EXPLANATION_MAX} characters.`);
      return;
    }

    const used = s.lines.filter((l) => l.pickKey && (Number(l.debit) > 0 || Number(l.credit) > 0));

    if (used.length < 2) {
      notifyError('A journal needs at least two lines to form a double entry.');
      return;
    }

    // The server re-checks this; the message here just saves a round trip.
    if (post && !isBalanced) {
      notifyError('The journal does not balance.');
      return;
    }

    isSaving.current = true;
    setBusy(true);

    // ap5-a6: turn any chart-only picks into period nominals before the journal is written.
    let resolved: EditorLine[];
    try {
      resolved = await resolveChartPicks(used);
    } catch (error) {
      fail(error);
      return;
    }

    const lines = resolved
      .filter((l) => l.nominalAccountId)
      .map((l) => ({
        nominalAccountId: l.nominalAccountId,
        reportingCodeId: null,
        debit: Number(l.debit || 0),
        credit: Number(l.credit || 0),
        narrative: l.narrative,
      }));

    if (lines.length < 2) {
      fail('A journal needs at least two lines to form a double entry.');
      return;
    }

    const current = ref.current;
    try {
      const saved = data.journalId
        ? await phase1Service.saveJournal(data.periodId, data.journalId, {
            journalDate: current.journalDate,
            narrative: current.narrative?.trim() || null,
            lines,
          })
        : await phase1Service.createJournal(data.periodId, {
            typeCode: current.typeCode,
            journalDate: current.journalDate,
            narrative: current.narrative?.trim() || null,
            lines,
          });
      if (post) {
        try {
          await phase1Service.postJournal(data.periodId, saved.journalId, saved.eTag);
        } catch (error) {
          fail(error);
          return;
        }
        await finish(saved.journalId, 'Journal posted.');
      } else {
        await finish(saved.journalId, 'Journal saved.');
      }
    } catch (error) {
      fail(error);
    }
  };

  return (
    <div className="mat-dialog-wrapper new-account journal-dialog">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>{!data?.journalId ? 'Add Journal' : readOnly ? 'View Journal' : 'Edit Journal'}</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>

          <div className="sidenav-body">
            {readOnly && (
              <p className="text-gray font-size-12" role="note">
                This journal is posted. To change it, reverse it and post a correcting journal.
              </p>
            )}
            {/* APT336, the client's wording: a posted journal is never deleted or voided; a correction is a reversal. */}
            {!readOnly && (
              <p className="text-gray font-size-12" role="note">
                Journal adjustments can't be deleted so make sure the correct data is entered. If you need to make a correction, reverse the
                incorrect journal and start again.
              </p>
            )}
            {!data?.journalId && (
              <div className="form-field">
                <p className="fw-bold">Type</p>
                <Select
                  className="w-434"
                  openOnFocus
                  panelClass="myPanelClass"
                  items={data?.types ?? []}
                  bindLabel="name"
                  bindValue="typeCode"
                  clearable={false}
                  name="typeCode"
                  value={m.typeCode}
                  onChange={(value) => set({ typeCode: value as string })}
                />
              </div>
            )}

            <div className="form-field">
              <p className="fw-bold">
                Date <RequiredMark />
              </p>
              <DateField
                fieldClassName="w-434"
                placeholder="dd/mm/yyyy"
                name="journalDate"
                value={m.journalDate || null}
                disabled={readOnly}
                invalid={!readOnly && (m.dateInvalid !== null || (m.submitted && !m.journalDate))}
                onChange={(date) => set({ journalDate: date ? date.format('YYYY-MM-DD') : '' })}
                onInvalidText={(text) => set({ dateInvalid: text })}
              />
              {m.dateInvalid !== null && <div className="text-red font-size-12">Enter the date as dd/mm/yyyy, for example 31/03/2026.</div>}
            </div>

            <div className="form-field">
              <p className="fw-bold">Narrative</p>
              <InputField
                fieldClassName="w-434"
                name="narrative"
                maxLength={JOURNAL_NARRATIVE_MAX}
                value={m.narrative}
                disabled={readOnly}
                onChange={(event) => set({ narrative: event.target.value })}
              />
            </div>

            {/* APT338 follow-up 2 (the client, 28 Oct 2026 21:35): "for that journal they can add evidences & explanations". */}
            {target && (
              <div className="form-field textarea">
                <p className="fw-bold">
                  Explanation <RequiredMark />
                </p>
                <TextareaField
                  fieldClassName="w-434"
                  rows={3}
                  maxLength={JOURNAL_EXPLANATION_MAX}
                  invalid={m.submitted && !m.explanation.trim()}
                  name="explanation"
                  value={m.explanation}
                  onChange={(event) => set({ explanation: event.target.value })}
                />
                <div className="mt-5">
                  <input
                    ref={fileInput}
                    type="file"
                    multiple
                    className="d-none"
                    accept={EVIDENCE_FILE_ACCEPT}
                    onChange={(event) => onEvidenceFilesChange(event.target)}
                  />
                  <Button className="action-button secondary-button border-button mr-1" onClick={() => fileInput.current?.click()}>
                    <Icon name="attach_file" outlined />
                    Attach evidence
                  </Button>
                  {m.evidenceFiles.map((file, index) => (
                    <span key={`${file.name}-${index}`} className="mr-1">
                      {file.name}{' '}
                      <Button
                        title={`Remove ${file.name}`}
                        onClick={() => set({ evidenceFiles: ref.current.evidenceFiles.filter((_, i) => i !== index) })}
                      >
                        <Icon name="close" />
                      </Button>
                    </span>
                  ))}
                </div>
                <p className="text-gray font-size-12">
                  The explanation and the files are kept with this journal and on the working paper. A PDF, image, CSV, Excel or Word file up to 10 MB each.
                </p>
              </div>
            )}

            {data?.journalId && m.journalEvidence.length > 0 && (
              <div className="form-field">
                <p className="fw-bold">Explanation and evidence</p>
                <ul className="journal-evidence">
                  {m.journalEvidence.map((item) => (
                    <li key={item.id}>
                      {item.title}
                      {item.detail ? `: ${item.detail}` : ''}
                      {item.hasFile && item.fileName ? ` (${item.fileName})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="fw-bold">Lines</p>
            {/* task-129: a way out of the empty picker, not just an explanation of it - shown here so it does not
                need the dropdown opened first. The dropdown's own not-found text below is unchanged. */}
            {!m.isLoadingPicks && m.picks.length === 0 && (
              <p className="text-gray font-size-12">
                This client has no nominal codes to pick from yet. Add its chart of accounts, or commit a trial balance
                import.{' '}
                <Button className="link-button" onClick={openChartOfAccounts}>
                  Open Chart of Accounts
                </Button>
              </p>
            )}
            <div className="journal-lines-wrapper">
              <table className="wp-100 journal-lines">
                <tbody>
                  <tr>
                    <th className="col-nominal">Nominal</th>
                    <th className="col-amount">This year</th>
                    <th className="col-amount">Prior year</th>
                    <th className="col-entry">Debit</th>
                    <th className="col-entry">Credit</th>
                    <th className="col-narrative">Narrative</th>
                    <th className="col-action"></th>
                  </tr>
                  {m.lines.map((line, i) => {
                    const pick = line.pickKey ? (m.picks.find((p) => p.key === line.pickKey) ?? null) : null;
                    return (
                      <tr key={line.uid}>
                        <td className="col-nominal">
                          {/* ap5-a6: searchable over the period's nominals *and* the client's chart of accounts, so a
                            code the trial balance import never carried can still be used. */}
                          <Select<NominalPick>
                            className="w-250"
                            panelClass="myPanelClass"
                            placeholder="Search code or name"
                            notFoundText={
                              m.isLoadingPicks && m.picks.length === 0
                                ? 'Loading...'
                                : m.picks.length === 0
                                  ? // task-129: nothing to pick is not the same as nothing matching the search.
                                    'No nominals yet. Add the client’s chart of accounts, or commit a trial balance import.'
                                  : 'No matching account'
                            }
                            items={m.picks}
                            bindLabel="searchText"
                            bindValue="key"
                            searchable
                            disabled={readOnly}
                            value={line.pickKey}
                            onChange={(value) => onPickChange(line, (value as string | null) ?? null)}
                            // Blank until the nominal list has loaded: a saved line's account is not in it for a moment after the
                            // dialog opens, and the label read "undefined - undefined".
                            renderLabel={(item) => (item?.code ? `${item.code} - ${item.description}` : '')}
                            renderOption={(item) => (
                              <>
                                {item.code} - {item.description}{' '}
                                {item.inPeriod && (
                                  <span className="text-gray">
                                    ({formatNatural(item.adjustedAmount, item.normalSign)} / {formatNatural(item.priorYearAmount, item.normalSign)})
                                  </span>
                                )}
                                {!item.inPeriod && <span className="text-gray"> (not yet in this period)</span>}
                              </>
                            )}
                          />
                        </td>
                        <td className="col-amount">
                          {pick && (
                            <span>
                              {pick.inPeriod ? formatNatural(pick.adjustedAmount, pick.normalSign) : <span className="text-gray">&mdash;</span>}
                            </span>
                          )}
                        </td>
                        <td className="col-amount">
                          {pick && (
                            <span>
                              {pick.inPeriod ? formatNatural(pick.priorYearAmount, pick.normalSign) : <span className="text-gray">&mdash;</span>}
                            </span>
                          )}
                        </td>
                        <td className="col-entry">
                          <InputField
                            fieldClassName="w-100"
                            {...AMOUNT_BOX}
                            value={line.debitText ?? numberToInput(line.debit)}
                            disabled={readOnly}
                            onChange={(event) =>
                              editLine(line.uid, { debit: amountFromText(event.target.value), debitText: event.target.value })
                            }
                          />
                        </td>
                        <td className="col-entry">
                          <InputField
                            fieldClassName="w-100"
                            {...AMOUNT_BOX}
                            value={line.creditText ?? numberToInput(line.credit)}
                            disabled={readOnly}
                            onChange={(event) =>
                              editLine(line.uid, { credit: amountFromText(event.target.value), creditText: event.target.value })
                            }
                          />
                        </td>
                        <td className="col-narrative">
                          <InputField
                            fieldClassName="w-200"
                            maxLength={JOURNAL_NARRATIVE_MAX}
                            value={line.narrative ?? ''}
                            disabled={readOnly}
                            onChange={(event) => editLine(line.uid, { narrative: event.target.value })}
                          />
                        </td>
                        <td className="col-action">
                          {!readOnly && (
                            <Button title="Remove" onClick={() => removeLine(i)}>
                              <Icon name="delete_outline" outlined />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {!readOnly && (
              <Button className="action-button secondary-button" onClick={addLine}>
                <Icon name="add" />
                <span>Add Line</span>
              </Button>
            )}

            {/* ap5-a6: the totals used to be three bare spans with no boxes or alignment (item 128). */}
            <div className="journal-totals">
              <span className="total-cell">
                <span className="total-label">Debit</span>
                {fmt2(totalDebit)}
              </span>
              <span className="total-cell">
                <span className="total-label">Credit</span>
                {fmt2(totalCredit)}
              </span>
              <span className={`total-status ${isBalanced ? 'text-green' : 'text-red'}`}>
                {isBalanced ? 'Balanced' : 'Out of balance by ' + fmt2(difference)}
              </span>
            </div>
          </div>

          <div className="sidenav-footer">
            <div className="d-flex align-items-center gap-10">
              {!readOnly && (
                <Button className="action-button primary-button" busy={busy} onClick={() => void onSave(false)}>
                  Save Draft
                </Button>
              )}
              {!readOnly && (
                <Button className="action-button primary-button" busy={busy} onClick={() => void onSave(true)}>
                  Save and Post
                </Button>
              )}
              <CancelButton />
            </div>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
